import { Formik } from "formik";
import { FC, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../config/Api";
import Loading from "../../container/Loading";
import { addOrEditJobValidation, NZTimeZone } from "../../helper/constant";
import { AddEditJobInterface, StateInterface } from "../../helper/interface";
import useFetch from "../../hooks/useFetch";
import usePathName from "../../hooks/usePathName";
import { AlertTriangle, ArrowLeftCircle } from "react-feather";
import AddEditJob from "./AddEditJob";
import { onEditJob } from "../../service/job";
import { JobPayloadFn } from "./Utils";
import { default as momentTimeZone } from "moment-timezone";

const EditJob: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { jobId = "" } = useParams();

    const { res, loading, error } = useFetch<AddEditJobInterface>(`${api.admin.job}/${jobId}`);

    const tableData = useSelector((state: StateInterface) => state.base.tableData);

    usePathName([
        { pathName: "Job", href: `/job/${tableData.tabFor}` },
        { pathName: "Edit", href: `/job/edit/${jobId}` },
    ]);

    const onSubmit = (values: AddEditJobInterface) => {
        const payload = JobPayloadFn(values);
        onEditJob(payload, +jobId, dispatch, navigate, tableData.tabFor);
    };

    const initialValues = useMemo<AddEditJobInterface | undefined>(() => {
        if (!res?.data) return undefined;

        // The centre is the source of truth while editing, including for jobs
        // created before the job-level timeZone field was introduced.
        const timeZone = res.data.centreForJob?.timeZone ?? res.data.timeZone ?? NZTimeZone;
        const startDate = momentTimeZone.utc(res.data.startDate).tz(timeZone);
        const endDate = momentTimeZone.utc(res.data.endDate).tz(timeZone);

        return {
            ...res.data,
            startDate: new Date(`${startDate.format("YYYY-MM-DD")}T00:00:00`),
            endDate: new Date(`${endDate.format("YYYY-MM-DD")}T00:00:00`),
            startTime: startDate.format("HH:mm"),
            endTime: endDate.format("HH:mm"),
        };
    }, [res]);

    return (
        <div className="intro-y box py-10 sm:py-10 mt-5">
            <div className="flex w-full items-center">
                <div className="flex-2 ml-4">
                    <ArrowLeftCircle className="cursor-pointer text-primary" onClick={() => navigate(`/job/${tableData.tabFor}`)} />
                </div>
                <div className="flex-1 font-medium text-center text-lg -ml-2">Edit job</div>
            </div>
            <div className="px-5 sm:px-20 mt-10 pt-10 border-t border-slate-200/60 dark:border-darkmode-400">
                {error ? (
                    <div className="alert alert-danger mt-6 show flex items-center mb-2" role="alert">
                        <AlertTriangle className="w-6 h-6 mr-2" /> {error}
                    </div>
                ) : loading ? (
                    <Loading />
                ) : initialValues ? (
                    <Formik initialValues={initialValues} validationSchema={addOrEditJobValidation} onSubmit={onSubmit}>
                        {(props) => <AddEditJob {...props} />}
                    </Formik>
                ) : null}
            </div>
        </div>
    );
};

export default EditJob;
